import { Color } from '@tmh-website/shared/colors';
import { resetButtonStylingCSS } from '@tmh-website/styles/global';
import StyledTypo from '@tmh-website/styles/typo';
import { buttonThemes } from '@tmh-website/ui/Button';
import { StyledLoadingSpinner } from '@tmh-website/ui/LoadingSpinner';
import { getScrollbarStylingCss } from '@tmh-website/ui/Scrollbar';
import transparentize from 'polished/lib/color/transparentize';
import styled, { css } from 'styled-components';

export const HeaderContainer = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column-reverse;
  padding: 16px ${props => props.theme.wrapperPaddingMobile} 20px;
  @media (${props => props.theme.breakpoint.sm.up}) {
    flex-direction: column;
    padding: 20px ${props => props.theme.wrapperPaddingMobile} 16px;
  }
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: 20px ${props => props.theme.wrapperPadding} 16px;
  }
`;

export const SearchField = styled.div`
  display: flex;
  gap: ${props => props.theme.gap.xSmall};
  align-items: center;
  margin-top: 16px;
  @media (${props => props.theme.breakpoint.sm.up}) {
    margin-top: 0;
    margin-bottom: 16px;
  }

  &:last-child {
    @media (${props => props.theme.breakpoint.sm.down}) {
      margin-top: 4px;
    }
    @media (${props => props.theme.breakpoint.sm.up}) {
      margin-bottom: 4px;
    }
  }

  ${StyledLoadingSpinner.Spinner} {
    margin-top: 2px;
  }

  > svg {
    flex-shrink: 0;
  }
`;

export const SearchInput = styled.input`
  ${StyledTypo.uiTextCss};
  appearance: none;
  width: 100%;
  min-height: 24px;
  padding: 8px 0;
  margin: -8px 0;
  color: ${props => props.theme.color[Color.Blue_1000_Deep]};
  background: transparent;
  border: none;
  outline: none;

  &::placeholder {
    color: ${props => transparentize(0.8, props.theme.color[Color.Blue_1000_Deep])};
  }
`;

export const TermsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.gap.xSmall};
  align-items: center;
`;

export const FilterButton = styled.button<{ $isActive: boolean }>`
  ${resetButtonStylingCSS};
  display: flex;
  align-items: center;
  padding: 10px;
  margin: -10px;
  color: ${props =>
    props.$isActive
      ? props.theme.color[Color.Blue_500_Ultramarine]
      : transparentize(0.4, props.theme.color[Color.Blue_1000_Deep])};
  background-color: ${props => props.theme.color[buttonThemes[Color.Neutral_0_White].background.normal]};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: ${props => props.theme.transitionTiming};

  &:hover {
    background-color: ${props => props.theme.color[buttonThemes[Color.Neutral_0_White].background.hover]};
  }

  &:active {
    background-color: ${props => props.theme.color[buttonThemes[Color.Neutral_0_White].background.active]};
  }

  > svg {
    display: block;
  }
`;

export const FiltersCount = styled.span`
  ${StyledTypo.uiTextSmallCss};
  display: block;
  min-width: 24px;
  height: 24px;
  padding: 2px;
  margin-right: 4px;
  color: ${props => props.theme.color[Color.Blue_500_Ultramarine]};
  text-align: center;
  background-color: ${props => props.theme.color[Color.Neutral_200_OffWhite]};
  border-radius: ${({ theme }) => (theme.isBorderRadiusDisabled ? 0 : '99999px')};
`;

export const ResultsContainer = styled.div<{ $extraPaddingTop: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-height: 70dvh;
  ${props => props.$extraPaddingTop && 'padding-top: 10px;'};
  overflow: hidden;
  border-bottom: 2px solid ${props => props.theme.color[Color.Neutral_300]};
  @media (${props => props.theme.breakpoint.sm.up}) {
    flex-direction: row;
    height: auto;
    border-top: 2px solid ${props => props.theme.color[Color.Neutral_300]};
    border-bottom: none;
  }
`;

export const ResultsEntries = styled.div<{ $noResults: boolean; $resultsOnBoth: boolean }>`
  position: relative;
  flex-basis: ${props => (props.$noResults ? '100px' : props.$resultsOnBoth ? '50%' : 'auto')};
  flex-shrink: ${props => (props.$noResults ? 0 : 'auto')};
  overflow-y: auto;
  border-top: 1px solid ${props => props.theme.color[Color.Neutral_300]};
  border-bottom: 1px solid ${props => props.theme.color[Color.Neutral_300]};
  ${getScrollbarStylingCss(Color.Neutral_0_White)};
  @media (${props => props.theme.breakpoint.sm.up}) {
    flex-basis: 50%;
    flex-grow: 1;
    width: 100%;
    border-top: none;
    border-right: 1px solid ${props => props.theme.color[Color.Neutral_300]};
    border-bottom: none;
    border-left: 1px solid ${props => props.theme.color[Color.Neutral_300]};
  }

  &:first-child {
    border-top: none;
    border-left: none;
  }

  &:last-child {
    border-right: none;
    border-bottom: none;
  }
`;

export const ResultsTitle = styled(StyledTypo.Typography)`
  position: sticky;
  top: 0;
  z-index: 2;
  display: block;
  width: 100%;
  padding: 12px 16px 4px;
  background-color: ${props => props.theme.color[Color.Neutral_0_White]};
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: 12px ${props => props.theme.wrapperPaddingMobile} 4px;
  }
`;

export const ResultsFade = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  height: 48px;
  margin-top: -24px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    ${props => transparentize(1, props.theme.color[Color.Neutral_0_White])} 20%,
    ${props => props.theme.color[Color.Neutral_0_White]} 100%
  );
`;

export const ErrorMessage = styled(StyledTypo.Typography)<{ $onlyErrorMessage?: boolean }>`
  align-items: center;
  width: 100%;
  min-height: 24px;
  padding: 0 ${props => props.theme.wrapperPaddingMobile};
  margin: 10px 0 0;
  text-align: center;
  @media (${props => props.theme.breakpoint.sm.up}) {
    padding: ${props => `20px ${props.theme.wrapperPaddingMobile}`};
    margin: 0;
  }
  @media (${props => props.theme.breakpoint.md.up}) {
    min-height: 105px;
    padding: ${props => `30px ${props.theme.wrapperPadding}`};
  }

  ${props =>
    props.$onlyErrorMessage &&
    css`
      @media (${props => props.theme.breakpoint.sm.down}) {
        padding-top: 20px;
        padding-bottom: 20px;
      }
    `}
`;
